import React, { PropsWithChildren, useCallback } from 'react';
import styled from '@emotion/styled';
import { Centered } from 'its-pages';
import { Box, Button, Checkbox, FormControlLabel, styled as muiStyled } from '@mui/material';
import { setToursShown } from '../redux/actions/app-actions';
import { addTour, clearTour } from '../redux/reducers/app-reducers';
import { useTour } from '@reactour/tour';
import { useClientDispatch, useClientSelector } from '../redux/hooks';
import { type Theme } from '@lib/theme/theme';

const WrapperGrid = muiStyled(Centered)`
  font-size: .8rem;
  display: grid;
  grid-template-areas:
    'content'
    'footer';
  grid-template-rows: max-content max-content;
  color: ${(props) => (props.theme as Theme).palette.popover.contrastText};
`;

const ContentArea = styled.div`
  grid-area: content;
  comment: content;
  margin: 0.5rem 0 0.25rem 0;
  max-width: min(70vw, 400px);
`;

const FooterArea = styled.div`
  grid-area: footer;
  comment: footer;
  width: 100%;
  display: grid;
  grid-template-areas: 'toc start-tour';
  grid-template-columns: 1fr 1fr;
`;

type Props = PropsWithChildren;

export const TourWrapper = (props: Props) => {
  const { children } = props;
  const dispatch = useClientDispatch();
  const toursShown = useClientSelector(state => state.firebase?.currentUser?.toursShown || []);

  const { setCurrentStep, currentStep } = useTour();

  const mainIndex = toursShown.indexOf('start-up');
  const hasMain = mainIndex !== -1;

  const onChange = useCallback(() => {
    const newToursShown = hasMain
      ? clearTour('start-up', toursShown)
      : addTour('start-up', toursShown);
    dispatch(setToursShown(newToursShown));
  }, [toursShown, hasMain, dispatch]);

  const onClick = useCallback(() => {
    setCurrentStep(0);
  }, [setCurrentStep]);

  return (
    <WrapperGrid>
      <ContentArea>{children}</ContentArea>

      <FooterArea>
        {currentStep !== 0 ? (
          <Button
            onClick={onClick}
            sx={{ alignSelf: 'start', justifySelf: 'start', gridArea: 'toc', p: 0 }}
          >
            Show ToC
          </Button>
        ) : null}
        <FormControlLabel
          sx={{
            margin: 0,
            alignSelf: 'end',
            justifySelf: 'end',
            gridArea: 'start-tour',
          }}
          control={<Checkbox checked={!hasMain} onChange={onChange} sx={{ p: 0 }} />}
          label={
            <Box component={'div'} fontSize={11}>
              Show Tour on Start
            </Box>
          }
        />
      </FooterArea>
    </WrapperGrid>
  );
};
